import {
  useEffect, useRef, useState,
} from 'react'
import {
  loadStripe, type Stripe, type StripeElements, type Appearance, type CustomFontSource,
} from '@stripe/stripe-js'
import {
  X,
} from 'lucide-react'

interface RateInfo {
  offer_id: string
  price: number
  currency: string
  free_cancel_until: string | null
  room_name: string
  board_name: string | null
  // Collected by the property at check-in, NOT charged to the card here. The
  // offer was chosen on price plus this, so a checkout showing only `price`
  // hides a charge already counted against the offers it beat (GET-11449).
  excluded_taxes_and_fees?: number | null
  excluded_taxes_currency?: string | null
  stripe_publishable_key: string
}

interface HotelBookingModalProps {
  rate: RateInfo
  hotelName: string
  /**
   * Where to redirect after Stripe authorization. The page at this URL is
   * responsible for parsing prebook_id, transaction_id, payment_intent and
   * calling POST /api/v1/hotels/complete-booking server-side.
   */
  returnUrlBase: string
  /**
   * Backend endpoint that creates the prebook on Pay-click.
   * Defaults to '/api/v1/hotels/prebook'.
   */
  prebookEndpoint?: string
  apiBaseUrl: string
  authToken: string
  onClose: () => void
  /**
   * Optional: extra context to round-trip through Stripe's return_url so the
   * /complete-booking call can persist the booking with the right metadata.
   */
  bookingContext: {
    hotel_id: string
    hotel_name: string
    checkin: string
    checkout: string
    adults: number
    children: number
    guest_first_name: string
    guest_last_name: string
    guest_email: string
  }
}

/**
 * Söhne, served to Stripe's iframe.
 *
 * Elements renders on js.stripe.com, which cannot see this app's @font-face
 * rules — naming a brand family in `appearance` alone resolves to nothing, which
 * is why this form was the one surface in the app set in the OS font. A
 * CustomFontSource is fetched by the iframe itself, so the URL must be absolute
 * (built from the current origin, since this runs on localhost in development)
 * and the response CORS-readable (see the fonts block in nginx.conf).
 *
 * If the fetch fails the stack in `fontFamily` below still applies, so the worst
 * case is exactly today's rendering.
 */
function brandFontSource(): CustomFontSource {
  return {
    family: 'Söhne',
    src: `url(${window.location.origin}/fonts/soehne-buch.woff2)`,
    weight: '400',
  }
}

const APPEARANCE: Appearance = {
  theme: 'stripe',
  variables: {
    colorPrimary: '#E55050',
    colorBackground: '#FAF7F7',
    colorText: '#212121',
    colorTextSecondary: '#6B7280',
    fontFamily: 'Söhne, system-ui, -apple-system, sans-serif',
    fontSizeBase: '15px',
    spacingUnit: '4px',
    borderRadius: '10px',
  },
  rules: {
    '.Input': { padding: '12px 14px' },
    '.Tab--selected': { borderColor: '#E55050' },
    '.Label': { fontWeight: '500', fontSize: '13px', marginBottom: '6px' },
  },
}

export function HotelBookingModal({
  rate, hotelName, returnUrlBase, prebookEndpoint = '/api/v1/hotels/prebook',
  apiBaseUrl, authToken, onClose, bookingContext,
}: HotelBookingModalProps): JSX.Element {
  const elementContainer = useRef<HTMLDivElement | null>(null)
  const [stripeReady, setStripeReady] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const elementsRef = useRef<{ stripe: Stripe; elements: StripeElements } | null>(null)

  // Mount Stripe Elements in DEFERRED mode (no clientSecret yet).
  useEffect(() => {
    let cancelled = false
    void (async () => {
      const stripe = await loadStripe(rate.stripe_publishable_key)
      if (cancelled || !stripe || !elementContainer.current) return
      const elements = stripe.elements({
        mode: 'payment',
        amount: Math.round(rate.price * 100),
        currency: rate.currency.toLowerCase(),
        captureMethod: 'manual',  // matches LiteAPI's PaymentIntent
        appearance: APPEARANCE,
        fonts: [brandFontSource()],
      })
      const paymentEl = elements.create('payment')
      paymentEl.mount(elementContainer.current)
      paymentEl.on('ready', () => {
        if (!cancelled) setStripeReady(true)
      })
      elementsRef.current = { stripe, elements }
    })()
    return () => { cancelled = true }
  }, [rate.stripe_publishable_key, rate.price, rate.currency])

  const handlePay = async () => {
    if (!elementsRef.current) return
    setError(null)
    setSubmitting(true)
    try {
      // 1. Validate the card form locally (no API call yet).
      const submitResult = await elementsRef.current.elements.submit()
      if (submitResult.error) {
        setError(submitResult.error.message ?? 'Card form invalid')
        return
      }

      // 2. Create prebook NOW. Clock starts here (~5–10s budget until /rates/book).
      const prebookRes = await fetch(`${apiBaseUrl}${prebookEndpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify({ offer_id: rate.offer_id }),
      })
      if (!prebookRes.ok) {
        const detail = await prebookRes.text()
        throw new Error(`Prebook failed: ${detail}`)
      }
      const prebook = await prebookRes.json() as {
        prebook_id: string; transaction_id: string; secret_key: string
      }

      // 3. Round-trip the booking context through return_url so /confirm can
      //    call /complete-booking server-side without re-fetching.
      const returnUrl = new URL(returnUrlBase, window.location.origin)
      returnUrl.searchParams.set('prebook_id', prebook.prebook_id)
      returnUrl.searchParams.set('transaction_id', prebook.transaction_id)
      returnUrl.searchParams.set('hotel_id', bookingContext.hotel_id)
      returnUrl.searchParams.set('hotel_name', bookingContext.hotel_name)
      returnUrl.searchParams.set('checkin', bookingContext.checkin)
      returnUrl.searchParams.set('checkout', bookingContext.checkout)
      returnUrl.searchParams.set('adults', String(bookingContext.adults))
      returnUrl.searchParams.set('children', String(bookingContext.children))
      returnUrl.searchParams.set('price', String(rate.price))
      returnUrl.searchParams.set('currency', rate.currency)
      returnUrl.searchParams.set('guest_first_name', bookingContext.guest_first_name)
      returnUrl.searchParams.set('guest_last_name', bookingContext.guest_last_name)
      returnUrl.searchParams.set('guest_email', bookingContext.guest_email)

      // 4. Confirm with Stripe — auth-only (capture happens after /rates/book).
      const result = await elementsRef.current.stripe.confirmPayment({
        elements: elementsRef.current.elements,
        clientSecret: prebook.secret_key,
        confirmParams: {
          return_url: returnUrl.toString(),
        },
      })
      if (result.error) {
        // Card declined / 3DS failed / etc — Stripe did NOT capture.
        setError(`${result.error.message ?? 'Payment failed'} — no charge made; prebook will expire.`)
      }
      // On success, browser is redirecting to return_url; nothing more to do here.
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Unexpected error')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative w-full max-w-lg max-h-[90vh] overflow-y-auto bg-white rounded-2xl shadow-xl m-4 p-6">
        <button
          onClick={onClose}
          className="absolute top-3 right-3 z-10 p-1 text-gray-400 hover:text-gray-600 transition-colors"
          aria-label="Close"
        >
          <X className="w-5 h-5" />
        </button>

        <h2 className="text-xl font-semibold text-gray-900">Confirm your stay</h2>
        <p className="mt-1 text-sm text-gray-600">{hotelName}</p>

        <div className="mt-4 rounded-lg border border-gray-200 p-3 text-sm">
          <div className="flex justify-between py-1">
            <span className="text-gray-600">Room</span>
            <span>{rate.room_name}</span>
          </div>
          <div className="flex justify-between py-1">
            <span className="text-gray-600">Check-in</span>
            <span>{bookingContext.checkin}</span>
          </div>
          <div className="flex justify-between py-1">
            <span className="text-gray-600">Check-out</span>
            <span>{bookingContext.checkout}</span>
          </div>
          {rate.free_cancel_until && (
            <div className="flex justify-between py-1">
              <span className="text-gray-600">Free cancellation</span>
              <span className="text-xs">until {rate.free_cancel_until}</span>
            </div>
          )}
          <div className="mt-2 flex justify-between border-t border-gray-200 pt-2 font-semibold">
            <span>Total</span>
            <span>${rate.price.toFixed(2)} {rate.currency}</span>
          </div>
          {rate.excluded_taxes_and_fees != null && rate.excluded_taxes_and_fees > 0 && (
            <div className="flex justify-between pt-1 text-xs text-gray-600">
              <span>Paid at the property</span>
              <span>
                {rate.excluded_taxes_and_fees.toFixed(2)}{' '}
                {rate.excluded_taxes_currency ?? rate.currency}
              </span>
            </div>
          )}
        </div>

        <div ref={elementContainer} className="mt-4 min-h-[200px]" />

        {error && (
          <p className="mt-3 text-sm text-red-600">{error}</p>
        )}

        <button
          type="button"
          onClick={handlePay}
          disabled={!stripeReady || submitting}
          className="mt-4 w-full rounded-xl bg-catch-red px-4 py-3 font-medium text-white hover:bg-catch-red/90 disabled:opacity-50"
        >
          {submitting ? 'Processing…' : !stripeReady ? 'Loading…' : `Pay $${rate.price.toFixed(2)}`}
        </button>

        <p className="mt-2 text-xs text-gray-500">
          You'll only be charged once the hotel confirms your room.
        </p>
      </div>
    </div>
  )
}
